import { useEffect, useMemo, useState } from 'react';
import type { Loan, LoanEvent } from '@/types';
import { listEvents } from '@/lib/db';
import { summarizeLoanState, describeEstimateBasis } from '@/lib/calc/loanState';
import { todayIso, isBefore } from '@/lib/calc/dates';
import { computeSavingsBaselines, computeSavingsSummary } from '@/lib/calc/savingsBaselines';
import { groupByFinancialYear } from './financialYearGrouping';
import { FinancialYearSection } from './components/FinancialYearSection';
import { formatInr, formatDate } from '@/lib/format';
import { ScheduleIcon } from '@/components/icons';

interface SchedulePageProps {
  loan: Loan;
}

/**
 * The Schedule screen. Per the Phase 2 architecture, this is a pure
 * PRESENTATION layer: it loads the loan's active events and calls the one
 * real calculation engine (computeSchedule / savingsBaselines) — no
 * calculation logic lives in this file.
 *
 * SUMMARY STRUCTURE:
 * The Summary is split into three sections, all consistently driven by
 * ONE user-selectable "As of" date (defaulting to today, never hard-
 * coded — see `asOfDate` state below):
 *
 *   - Current Position: computeSchedule({..., asOfDate: selectedDate}) —
 *     the loan's state AS OF the selected date. currentOutstandingPrincipal
 *     here is the balance on that date, never the final (zero) balance
 *     at eventual closure.
 *   - Current Projection: computeSchedule({...}) with NO asOfDate cap —
 *     the full, unrestricted trajectory to its own natural closure. A
 *     projected closure date from THIS call is always labelled
 *     "Projected Closure Date", never "Closed On" — "Closed On" only
 *     appears if the loan has ACTUALLY closed as of the selected date
 *     (i.e. the Current Position call itself reports isLoanClosed: true).
 *   - Savings: three baselines (A/B/C), each its own full computeSchedule()
 *     call via src/lib/calc/savingsBaselines.ts, compared via simple
 *     differences — never a shortcut formula, and never presenting a
 *     prepayment's face value as "savings".
 *
 * When the selected date is in the FUTURE (after today), the Current
 * Position section is explicitly relabelled "Projected Position" and
 * every figure in it is clearly marked as projected — it is never
 * presented as if it were today's actual, confirmed state.
 */
export function SchedulePage({ loan }: SchedulePageProps) {
  const [events, setEvents] = useState<LoanEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // The single "As of" date driving every as-of-date-dependent figure in
  // this page. Defaults to the app's actual runtime date (never hard-
  // coded) via todayIso(), and can be changed by the user via the date
  // picker below.
  const [asOfDate, setAsOfDate] = useState<string>(() => todayIso());

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    listEvents({ loanId: loan.id }).then((result) => {
      if (!cancelled) {
        setEvents(result);
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [loan.id]);

  // The earliest date the loan timeline could possibly be queried at —
  // mirrors the engine's own walk-start logic (earliest of
  // loan.loanStartDate and the first disbursement's date) so the date
  // picker's minimum bound matches exactly what the engine itself would
  // treat as "before this loan existed".
  const earliestValidDate = useMemo(() => {
    const firstDisbursementDate = events
      .filter((e) => e.type === 'disbursement' && !e.supersededAt)
      .map((e) => e.effectiveDate)
      .sort()[0];
    if (loan.loanStartDate && firstDisbursementDate) {
      return isBefore(firstDisbursementDate, loan.loanStartDate) ? firstDisbursementDate : loan.loanStartDate;
    }
    return loan.loanStartDate ?? firstDisbursementDate;
  }, [loan.loanStartDate, events]);

  const today = todayIso();
  const isFutureDate = asOfDate > today;

  const loanState = useMemo(() => {
    if (isLoading) return undefined;
    return summarizeLoanState({ loan, events, asOfDate });
  }, [loan, events, isLoading, asOfDate]);

  const currentPosition = loanState?.position;
  const currentProjection = loanState?.projection;

  const savings = useMemo(() => {
    if (isLoading) return undefined;
    const baselines = computeSavingsBaselines(loan, events);
    return computeSavingsSummary(baselines);
  }, [loan, events, isLoading]);

  // The FY-collapsible schedule itself is still driven by the full,
  // unrestricted projection — it shows the whole trajectory, past and
  // future alike, independent of the selected "As of" date.
  const groups = useMemo(() => (currentProjection ? groupByFinancialYear(currentProjection.months) : []), [currentProjection]);

  function handleAsOfDateChange(next: string) {
    if (!next) return;
    if (earliestValidDate && next < earliestValidDate) {
      // Never allow selecting a date before the loan timeline starts.
      return;
    }
    setAsOfDate(next);
  }

  if (isLoading) {
    return (
      <div className="stack">
        <h1>Schedule</h1>
        <p className="text-muted">Loading…</p>
      </div>
    );
  }

  if (!loanState || !currentPosition || !currentProjection || !savings || groups.length === 0) {
    return (
      <div className="stack">
        <div>
          <h1>Schedule</h1>
          <p>Financial-Year → Month schedule view.</p>
        </div>
        <div className="empty-state">
          <div className="empty-state-icon">
            <ScheduleIcon size={28} />
          </div>
          <p>
            No schedule yet — record at least one disbursement on the Timeline (and a Loan Start Date on Settings) to
            see your month-by-month schedule here.
          </p>
        </div>
      </div>
    );
  }

  // Most recent FY starts expanded; older ones start collapsed, keeping
  // the initial view manageable for a decade-long loan.
  const mostRecentFinancialYear = groups[groups.length - 1]?.financialYear;

  return (
    <div className="stack">
      <div>
        <h1>Schedule</h1>
        <p>Financial-Year → Month schedule, calculated from your recorded events.</p>
      </div>

      <div className="card-supporting">
        <div className="field-group" style={{ marginBottom: 0 }}>
          <label htmlFor="asOfDate">As of</label>
          <input
            id="asOfDate"
            type="date"
            value={asOfDate}
            min={earliestValidDate}
            onChange={(e) => handleAsOfDateChange(e.target.value)}
          />
          {isFutureDate && (
            <span className="field-hint">
              You've selected a future date. The Position figures below are a PROJECTION for that date, not your
              confirmed current state.
            </span>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-title">{isFutureDate ? 'Projected Position' : 'Current Position'}</div>
        <p className="field-hint" style={{ marginTop: 0 }}>
          As of {formatDate(asOfDate)}
          {isFutureDate ? ' — projected, not yet actual.' : '.'}
        </p>
        <div className="stack" style={{ gap: 10 }}>
          <SummaryRow
            label={isFutureDate ? 'Projected Outstanding Principal' : 'Current Outstanding Principal'}
            value={formatInr(isFutureDate ? currentPosition.summary.currentOutstandingPrincipal : loanState.recordedPrincipal, true)}
            emphasize
          />
          <SummaryRow
            label={isFutureDate ? 'Projected Interest Rate' : 'Current Interest Rate'}
            value={`${currentPosition.summary.currentApplicableAnnualRatePercent.toFixed(2)}%`}
          />
          <SummaryRow
            label={isFutureDate ? 'Projected EMI' : 'Current EMI'}
            value={currentPosition.summary.isLoanClosed ? '—' : formatInr(currentPosition.summary.currentApplicableEmi, true)}
            est={loanState.regularEmi?.basis === 'derived_over_contract_tenure'}
          />
          {loanState.nextPayment && !currentPosition.summary.isLoanClosed && (
            <SummaryRow
              label={`Next Payment · ${formatDate(loanState.nextPayment.emiDate)}`}
              value={formatInr(loanState.nextPayment.emiAmount, true)}
              est
            />
          )}
          {loanState.lenderReported?.instalmentAmount !== undefined && (
            <SummaryRow label="LIC Reported Instalment" value={formatInr(loanState.lenderReported.instalmentAmount, true)} />
          )}
          <SummaryRow
            label="Remaining Tenure"
            value={currentPosition.summary.isLoanClosed ? '0 months' : `${loanState.remainingRegularInstalments ?? 0} months`}
          />
          {loanState.contractualMaturity && (
            <SummaryRow label="Contractual Maturity" value={formatDate(loanState.contractualMaturity.date)} />
          )}
          <SummaryRow
            label={isFutureDate ? 'Principal Repaid (Projected)' : 'Principal Repaid So Far'}
            value={formatInr(currentPosition.summary.totalPrincipalRepaid, true)}
          />
          {currentPosition.summary.totalPrepayments > 0 && (
            <SummaryRow
              label={isFutureDate ? 'Prepayments (Projected)' : 'Prepayments Made So Far'}
              value={formatInr(currentPosition.summary.totalPrepayments, true)}
            />
          )}
          {currentPosition.summary.isLoanClosed && currentPosition.summary.projectedClosureDate && (
            <SummaryRow
              label={isFutureDate ? 'Projected to Close On' : 'Closed On'}
              value={formatDate(currentPosition.summary.projectedClosureDate)}
            />
          )}
        </div>
      </div>

      {!currentPosition.summary.isLoanClosed && (
        <div className="card">
          <div className="card-title">Current Projection</div>
          <p className="field-hint" style={{ marginTop: 0 }}>
            Based on your latest recorded events, projected forward to closure. Independent of the "As of" date above.
          </p>
          <div className="stack" style={{ gap: 10 }}>
            <SummaryRow
              label="Projected Total Payment"
              value={formatInr(currentProjection.summary.totalEmiPaid + currentProjection.summary.totalPrepayments, true)}
            />
            <SummaryRow
              label="Projected Total Interest"
              value={formatInr(currentProjection.summary.projectedTotalInterest ?? currentProjection.summary.totalInterestPaid, true)}
              est
            />
            {currentProjection.summary.projectedClosureDate && (
              <SummaryRow label="Projected Closure Date" value={formatDate(currentProjection.summary.projectedClosureDate)} est />
            )}
          </div>
          {describeEstimateBasis(loanState.projectionInfo) && (
            <p className="field-hint" style={{ marginTop: 10, marginBottom: 0 }}>
              {describeEstimateBasis(loanState.projectionInfo)}
            </p>
          )}
        </div>
      )}

      <div className="card">
        <div className="card-title">Savings vs. Original Loan Plan</div>
        <p className="field-hint" style={{ marginTop: 0 }}>
          Compared with your original sanctioned terms, no later rate changes, and no prepayments.
        </p>
        <div className="stack" style={{ gap: 10 }}>
          <SummaryRow label="Interest Saved" value={formatInr(savings.interestSavedVsOriginal, true)} />
          <SummaryRow label="Total Payment Saved" value={formatInr(savings.totalPaymentSavedVsOriginal, true)} />
          <SummaryRow label="Time Saved" value={`${savings.timeSavedVsOriginalMonths} months`} />
        </div>
        <p className="field-hint" style={{ marginTop: 12, marginBottom: 0 }}>
          Of this, the effect of rate movements alone (independent of your prepayments/repayment decisions) is{' '}
          {formatInr(savings.interestSavedDueToRateMovementAlone, true)} in interest —{' '}
          {savings.interestSavedDueToRateMovementAlone >= 0 ? 'a saving' : 'an added cost'} from how the rate has moved.
        </p>
      </div>

      <div className="stack">
        {groups.map((group) => (
          <FinancialYearSection
            key={group.financialYear}
            group={group}
            defaultExpanded={group.financialYear === mostRecentFinancialYear}
          />
        ))}
      </div>
    </div>
  );
}

function SummaryRow({ label, value, emphasize, est }: { label: string; value: string; emphasize?: boolean; est?: boolean }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span className="text-muted">{label}</span>
      <span className={`mono${emphasize ? ' figure-secondary' : ''}`}>
        {est && (
          <span className="field-hint" title="Calculated estimate (working assumption)" style={{ marginRight: 6 }}>
            est.
          </span>
        )}
        {value}
      </span>
    </div>
  );
}
